import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatUSD, formatUSDPrecise } from "@/lib/api";
import { normalizeEstimate, safeString, safeNumber, safeArray } from "@/lib/safe";
import AppShell from "@/components/AppShell";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Printer, AlertCircle, Home, BedDouble,
  Bath, Ruler, RefreshCw, FileText,
} from "lucide-react";

const confidenceColor = {
  low: "border-red-500 text-red-400",
  medium: "border-amber-500 text-amber-400",
  high: "border-emerald-500 text-emerald-400",
};

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: p } = await api.get(`/projects/${projectId}`);
      setProject(p);
      try {
        const { data: e } = await api.get(`/projects/${projectId}/estimate`);
        setEstimate(normalizeEstimate(e));
      } catch (estimateErr) {
        // 404 is expected when no estimate has been generated yet
        if (estimateErr?.response?.status !== 404) {
          console.warn("Failed to load estimate:", estimateErr);
        }
        setEstimate(null);
      }
    } catch (err) {
      toast.error("Project not found");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Poll while analyzing
  useEffect(() => {
    if (project?.status !== "analyzing") return;
    const iv = setInterval(async () => {
      try {
        const { data: p } = await api.get(`/projects/${projectId}`);
        setProject(p);
        if (p.status === "ready") {
          const { data: e } = await api.get(`/projects/${projectId}/estimate`);
          setEstimate(normalizeEstimate(e));
          toast.success("Estimate ready");
          setRunning(false);
        } else if (p.status === "error") {
          setRunning(false);
          toast.error(p.error_message || "Estimate failed");
        }
      } catch (pollErr) {
        // Transient errors are expected during polling (network blips, etc.).
        // Log silently and keep polling.
        console.debug("Poll error (will retry):", pollErr?.message);
      }
    }, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.status, projectId]);

  const generate = async () => {
    setRunning(true);
    setEstimate(null);
    try {
      await api.post(`/projects/${projectId}/estimate`);
      // poll loop in useEffect will pick up the status change
      const { data: p } = await api.get(`/projects/${projectId}`);
      setProject(p);
      toast.success("Analysis started — this usually takes 60–120s");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Estimate kickoff failed");
      setRunning(false);
    }
  };

  const updateLine = (divIdx, liIdx, field, value) => {
    if (!estimate) return;
    const next = { ...estimate, divisions: estimate.divisions.map((d) => ({ ...d, line_items: [...d.line_items] })) };
    const li = { ...next.divisions[divIdx].line_items[liIdx], [field]: value };
    next.divisions[divIdx].line_items[liIdx] = li;
    setEstimate(next);
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      // make sure numeric fields are numbers
      const divisions = estimate.divisions.map((d) => ({
        ...d,
        line_items: d.line_items.map((li) => ({
          ...li,
          quantity: parseFloat(li.quantity) || 0,
          unit_cost: parseFloat(li.unit_cost) || 0,
          material_cost: parseFloat(li.material_cost) || 0,
          labor_cost: parseFloat(li.labor_cost) || 0,
          equipment_cost: parseFloat(li.equipment_cost) || 0,
        })),
      }));
      const { data } = await api.put(`/projects/${projectId}/estimate`, {
        divisions,
        overhead_pct: estimate.overhead_pct,
        profit_pct: estimate.profit_pct,
      });
      setEstimate(normalizeEstimate(data));
      toast.success("Estimate updated");
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <Skeleton className="h-8 w-64 bg-[#171A20] mb-4" />
        <Skeleton className="h-40 w-full bg-[#171A20] mb-4" />
        <Skeleton className="h-96 w-full bg-[#171A20]" />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div data-testid="project-detail-page">
        <button
          onClick={() => navigate("/dashboard")}
          className="text-sm text-slate-400 hover:text-white flex items-center gap-1 mb-6 transition-colors"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="h-4 w-4" /> Back to projects
        </button>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF6A00] font-semibold mb-2">
              {project.project_type.replace(/_/g, " ")}
            </div>
            <h1
              className="text-3xl md:text-4xl font-black tracking-tight text-white"
              style={{ fontFamily: "Chivo, sans-serif" }}
              data-testid="project-detail-name"
            >
              {project.name}
            </h1>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-slate-400">
              {project.address && <span>{project.address}</span>}
              {project.pdf_filename && (
                <span className="flex items-center gap-1">
                  <FileText className="h-3.5 w-3.5" /> {project.pdf_filename}
                  {project.pdf_pages ? ` · ${project.pdf_pages}p` : ""}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {estimate && (
              <Button
                variant="outline"
                onClick={() => window.print()}
                className="bg-transparent border-[#2A2F38] text-slate-200 hover:bg-[#171A20]"
                data-testid="print-btn"
              >
                <Printer className="h-4 w-4 mr-2" /> Print
              </Button>
            )}
            <Button
              onClick={generate}
              disabled={running}
              className="bg-[#FF6A00] hover:bg-[#E65C00] text-white"
              data-testid="generate-estimate-btn"
            >
              {running ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Analyzing…
                </>
              ) : estimate ? (
                <>
                  <Sparkles className="h-4 w-4 mr-2" /> Regenerate
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" /> Run AI estimate
                </>
              )}
            </Button>
          </div>
        </div>

        {project.status === "error" && project.error_message && (
          <div
            className="border border-red-500/40 bg-red-500/5 p-4 mb-6 flex items-start gap-3"
            data-testid="project-error-banner"
          >
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-300">Last estimate failed</div>
              <div className="text-sm text-red-200/70 mt-1 whitespace-pre-wrap">{project.error_message}</div>
            </div>
          </div>
        )}

        {(running || project.status === "analyzing") && !estimate && (
          <div
            className="border border-[#2A2F38] bg-[#171A20] p-10 text-center mb-6"
            data-testid="analyzing-banner"
          >
            <div className="inline-flex h-12 w-12 rounded-full border-2 border-[#FF6A00] border-t-transparent animate-spin mb-4" />
            <h3 className="text-lg font-bold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
              TAKT. is analyzing your plans.
            </h3>
            <p className="mt-2 text-sm text-slate-400">
              Extracting dimensions, schedules and specs. Usually 60–120 seconds.
              <br />
              You can leave this page — the estimate will be waiting on your dashboard.
            </p>
          </div>
        )}

        {!estimate && !running && project.status !== "analyzing" && project.status !== "error" && (
          <div
            className="border border-dashed border-[#2A2F38] bg-[#171A20]/50 p-12 text-center"
            data-testid="no-estimate-state"
          >
            <Sparkles className="h-10 w-10 text-[#FF6A00] mx-auto" />
            <h3 className="mt-4 text-lg font-bold text-white" style={{ fontFamily: "Chivo, sans-serif" }}>
              Ready to estimate
            </h3>
            <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">
              Hit "Run AI estimate" to send these plans through TAKT. and produce a
              full division-by-division cost sheet.
            </p>
          </div>
        )}

        {estimate && (
          <ErrorBoundary>
            <>
            {/* Top summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#2A2F38] border border-[#2A2F38] mb-6">
              {[
                { l: "SqFt", v: estimate.scope_summary?.total_sqft, icon: Ruler },
                { l: "Bedrooms", v: estimate.scope_summary?.bedrooms, icon: BedDouble },
                { l: "Baths", v: estimate.scope_summary?.bathrooms, icon: Bath },
                { l: "Floors", v: estimate.scope_summary?.floors, icon: Home },
                {
                  l: "Confidence",
                  v: (
                    <Badge variant="outline" className={confidenceColor[estimate.confidence] || ""} data-testid="confidence-badge">
                      {safeString(estimate.confidence, "medium")}
                    </Badge>
                  ),
                  icon: Sparkles,
                  isComponent: true,
                },
              ].map((s) => (
                <div key={s.l} className="bg-[#171A20] p-4">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 flex items-center gap-1.5">
                    <s.icon className="h-3 w-3" /> {s.l}
                  </div>
                  <div
                    className="mt-2 text-xl font-bold text-white"
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                  >
                    {s.isComponent ? s.v : (s.v === null || s.v === undefined ? "—" : safeString(s.v, "—"))}
                  </div>
                </div>
              ))}
            </div>

            {/* Big totals strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#2A2F38] border border-[#2A2F38] mb-8">
              {[
                { l: "Subtotal", v: estimate.subtotal },
                { l: `Overhead (${estimate.overhead_pct}%)`, v: estimate.overhead_amount },
                { l: `Profit (${estimate.profit_pct}%)`, v: estimate.profit_amount },
                { l: "Total", v: estimate.total, highlight: true },
              ].map((s) => (
                <div key={s.l} className={`p-5 ${s.highlight ? "bg-[#FF6A00]/10 border-l-2 border-[#FF6A00]" : "bg-[#171A20]"}`}>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold">
                    {s.l}
                  </div>
                  <div
                    className={`mt-2 text-2xl md:text-3xl font-black ${s.highlight ? "text-[#FF6A00]" : "text-white"}`}
                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                    data-testid={`total-${s.l.toLowerCase().split(" ")[0]}`}
                  >
                    {formatUSD(s.v)}
                  </div>
                </div>
              ))}
            </div>

            {/* Scope summary */}
            {(estimate.scope_summary?.key_features?.length || estimate.assumptions?.length || estimate.scope_summary?.plan_notes?.length) ? (
              <div className="grid md:grid-cols-3 gap-px bg-[#2A2F38] border border-[#2A2F38] mb-8">
                {estimate.scope_summary?.key_features?.length > 0 && (
                  <div className="bg-[#171A20] p-5">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-3">
                      Key features
                    </div>
                    <ul className="space-y-1.5 text-sm text-slate-300">
                      {safeArray(estimate.scope_summary?.key_features).map((k, i) => {
                        const text = safeString(k);
                        return (
                          <li key={`kf-${i}-${text.slice(0, 20)}`} className="flex gap-2">
                            <span className="text-[#FF6A00]">▸</span>
                            <span>{text}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {estimate.scope_summary?.plan_notes?.length > 0 && (
                  <div className="bg-[#171A20] p-5">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-3">
                      Plan notes
                    </div>
                    <ul className="space-y-1.5 text-sm text-slate-300">
                      {safeArray(estimate.scope_summary?.plan_notes).map((k, i) => {
                        const text = safeString(k);
                        return (
                          <li key={`pn-${i}-${text.slice(0, 20)}`} className="flex gap-2">
                            <span className="text-[#FF6A00]">▸</span>
                            <span>{text}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {estimate.assumptions?.length > 0 && (
                  <div className="bg-[#171A20] p-5">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-3">
                      Estimator assumptions
                    </div>
                    <ul className="space-y-1.5 text-sm text-slate-300">
                      {safeArray(estimate.assumptions).map((k, i) => {
                        const text = safeString(k);
                        return (
                          <li key={`as-${i}-${text.slice(0, 20)}`} className="flex gap-2">
                            <span className="text-amber-400">⚠</span>
                            <span>{text}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            ) : null}

            {/* Divisions */}
            <div className="border border-[#2A2F38] bg-[#171A20]" data-testid="estimate-divisions">
              <div className="px-5 py-4 border-b border-[#2A2F38] flex items-center justify-between">
                <div>
                  <h2
                    className="text-xl font-bold text-white"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                  >
                    Line-item breakdown
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Click any value to edit. Totals recalculate on save.
                  </p>
                </div>
                <Button
                  onClick={saveEdits}
                  disabled={saving}
                  className="bg-[#FF6A00] hover:bg-[#E65C00] text-white text-sm"
                  data-testid="save-edits-btn"
                >
                  {saving ? "Saving…" : "Save edits"}
                </Button>
              </div>

              <Accordion type="multiple" defaultValue={safeArray(estimate.divisions).map((_, i) => `div-${i}`)}>
                {safeArray(estimate.divisions).map((div, divIdx) => {
                  const items = safeArray(div?.line_items);
                  const code = safeString(div?.code, "—");
                  const name = safeString(div?.name, "Untitled");
                  return (
                  <AccordionItem
                    key={`${code}-${name}-${divIdx}`}
                    value={`div-${divIdx}`}
                    className="border-b border-[#2A2F38] last:border-b-0"
                    data-testid={`division-${code}`}
                  >
                    <AccordionTrigger className="px-5 py-4 hover:bg-[#1F232B] hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-4">
                          <span
                            className="text-[#FF6A00] text-sm font-bold"
                            style={{ fontFamily: "JetBrains Mono, monospace" }}
                          >
                            {code}
                          </span>
                          <span className="text-white font-semibold text-base">{name}</span>
                          <Badge variant="outline" className="border-[#2A2F38] text-slate-400 text-xs">
                            {items.length} items
                          </Badge>
                        </div>
                        <span
                          className="text-white font-bold"
                          style={{ fontFamily: "JetBrains Mono, monospace" }}
                        >
                          {formatUSD(safeNumber(div?.subtotal))}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="bg-[#0F1115]">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-[#2A2F38] text-[10px] tracking-[0.2em] uppercase text-slate-500">
                              <th className="text-left px-4 py-2 font-semibold">Description</th>
                              <th className="text-right px-2 py-2 font-semibold w-20">Qty</th>
                              <th className="text-left px-2 py-2 font-semibold w-16">Unit</th>
                              <th className="text-right px-2 py-2 font-semibold w-28">Material</th>
                              <th className="text-right px-2 py-2 font-semibold w-28">Labor</th>
                              <th className="text-right px-2 py-2 font-semibold w-28">Equip.</th>
                              <th
                                className="text-right px-4 py-2 font-semibold w-32"
                                style={{ fontFamily: "JetBrains Mono, monospace" }}
                              >
                                Subtotal
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {items.length === 0 ? (
                              <tr>
                                <td colSpan={7} className="px-4 py-6 text-center text-slate-500 text-sm">
                                  No line items in this division
                                </td>
                              </tr>
                            ) : items.map((li, liIdx) => (
                              <tr
                                key={`${code}-li-${liIdx}`}
                                className="border-b border-[#2A2F38]/40 hover:bg-[#171A20]/50"
                                data-testid={`line-item-${divIdx}-${liIdx}`}
                              >
                                <td className="px-4 py-2">
                                  <Input
                                    value={safeString(li?.description)}
                                    onChange={(e) => updateLine(divIdx, liIdx, "description", e.target.value)}
                                    className="bg-transparent border-transparent hover:border-[#2A2F38] focus:border-[#FF6A00] text-slate-200 h-8 text-sm px-2"
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <Input
                                    type="number"
                                    value={safeNumber(li?.quantity)}
                                    onChange={(e) => updateLine(divIdx, liIdx, "quantity", e.target.value)}
                                    className="bg-transparent border-transparent hover:border-[#2A2F38] focus:border-[#FF6A00] text-right h-8 text-sm px-2 text-slate-200"
                                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <Input
                                    value={safeString(li?.unit, "EA")}
                                    onChange={(e) => updateLine(divIdx, liIdx, "unit", e.target.value)}
                                    className="bg-transparent border-transparent hover:border-[#2A2F38] focus:border-[#FF6A00] text-slate-300 h-8 text-sm px-2"
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <Input
                                    type="number"
                                    value={safeNumber(li?.material_cost)}
                                    onChange={(e) => updateLine(divIdx, liIdx, "material_cost", e.target.value)}
                                    className="bg-transparent border-transparent hover:border-[#2A2F38] focus:border-[#FF6A00] text-right h-8 text-sm px-2 text-slate-200"
                                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <Input
                                    type="number"
                                    value={safeNumber(li?.labor_cost)}
                                    onChange={(e) => updateLine(divIdx, liIdx, "labor_cost", e.target.value)}
                                    className="bg-transparent border-transparent hover:border-[#2A2F38] focus:border-[#FF6A00] text-right h-8 text-sm px-2 text-slate-200"
                                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                                  />
                                </td>
                                <td className="px-2 py-2">
                                  <Input
                                    type="number"
                                    value={safeNumber(li?.equipment_cost)}
                                    onChange={(e) => updateLine(divIdx, liIdx, "equipment_cost", e.target.value)}
                                    className="bg-transparent border-transparent hover:border-[#2A2F38] focus:border-[#FF6A00] text-right h-8 text-sm px-2 text-slate-200"
                                    style={{ fontFamily: "JetBrains Mono, monospace" }}
                                  />
                                </td>
                                <td
                                  className="text-right px-4 py-2 font-semibold text-white"
                                  style={{ fontFamily: "JetBrains Mono, monospace" }}
                                >
                                  {formatUSDPrecise(
                                    safeNumber(li?.material_cost) +
                                      safeNumber(li?.labor_cost) +
                                      safeNumber(li?.equipment_cost)
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  );
                })}
              </Accordion>
            </div>
            </>
          </ErrorBoundary>
        )}
      </div>
    </AppShell>
  );
}
